import { Skeleton } from "@/components/ui/skeleton";

interface CardSkeletonProps {
  showImage?: boolean;
  showFooter?: boolean;
  className?: string;
}

export function CardSkeleton({
  showImage = true,
  showFooter = true,
  className = "",
}: CardSkeletonProps) {
  return (
    <Skeleton className={`overflow-hidden h-full flex flex-col rounded-lg ${className}`}>
      {showImage && (
        // Make the image skeleton fill the entire top, including corners
        <div className="relative w-full -mt-px -ml-px -mr-px">
          <div className="block w-full aspect-video bg-white/[0.04]" />
        </div>
      )}

      <div className="pt-6 px-6">
        <div className="h-6 w-3/4 mb-2 bg-white/[0.04] rounded" />
        <div className="h-4 w-1/2 bg-white/[0.04] rounded" />
      </div>

      <div className="px-6 pb-6 flex-1 flex flex-col">
        <div className="h-4 w-full mb-2 bg-white/[0.04] rounded" />
        <div className="h-4 w-5/6 mb-4 bg-white/[0.04] rounded" />

        <div className="flex gap-2 mb-4">
          <div className="h-6 w-20 bg-white/[0.04] rounded" />
          <div className="h-6 w-24 bg-white/[0.04] rounded" />
          <div className="h-6 w-28 bg-white/[0.04] rounded" />
        </div>

        <div className="mt-auto">
          <div className="h-5 w-32 bg-white/[0.04] rounded" />
        </div>
      </div>

      {showFooter && (
        <div className="px-6 pb-6">
          <div className="h-10 w-full bg-white/[0.04] rounded" />
        </div>
      )}
    </Skeleton>
  );
}

// Studio Card Skeleton variant - matches new design with full-height image and overlaid content
// Visual hierarchy: name (0.15) > location (0.12) > description (0.10) > badge (0.08)
export function StudioCardSkeleton() {
  return (
    <Skeleton
      className="group overflow-hidden rounded-lg h-full flex flex-col"
      role="status"
      aria-label="Loading studio"
    >
      <div className="relative h-full">
        {/* Studio Photo - full card background with portrait aspect ratio */}
        <div className="-m-px aspect-[4/5] bg-white/[0.04]" />

        {/* Gradient overlay for text visibility - matches actual card */}
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />

        {/* Price badge - top right (slightly more visible for prominence) */}
        <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20">
          <div className="h-7 sm:h-8 w-24 sm:w-28 rounded-md bg-white/[0.08]" />
        </div>

        {/* Studio info - overlaid on bottom */}
        <div className="absolute inset-x-0 bottom-0 p-4 sm:p-6 space-y-2 z-10">
          {/* Studio name - primary element, highest visibility */}
          <div className="h-7 sm:h-8 w-2/3 bg-white/[0.15] rounded" />

          {/* Location - secondary element */}
          <div className="flex items-center gap-1.5">
            <div className="h-4 w-4 bg-white/[0.12] rounded" />
            <div className="h-4 w-2/5 bg-white/[0.12] rounded" />
          </div>

          {/* Description - tertiary element with staggered widths for natural look */}
          <div className="space-y-1.5 pt-1">
            <div className="h-3.5 w-[90%] bg-white/[0.10] rounded" />
            <div className="h-3.5 w-3/4 bg-white/[0.10] rounded" />
          </div>
        </div>
      </div>

      {/* Screen reader loading text */}
      <span className="sr-only">Loading studio information...</span>
    </Skeleton>
  );
}

// Instructor Card Skeleton - matches profile card with full-height image and overlaid name
export function InstructorCardSkeleton() {
  return (
    <Skeleton
      className="group overflow-hidden rounded-lg h-full flex flex-col"
      role="status"
      aria-label="Loading instructor"
    >
      <div className="relative h-full">
        {/* Profile Photo - full card background with portrait aspect ratio */}
        <div className="-m-px aspect-[3/4] bg-white/[0.04]" />

        {/* Gradient overlay for text visibility */}
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />

        {/* Name - overlaid on bottom */}
        <div className="absolute inset-x-0 bottom-0 p-4 z-10 flex justify-center">
          <div className="h-7 w-3/4 bg-white/[0.15] rounded" />
        </div>
      </div>

      <span className="sr-only">Loading instructor information...</span>
    </Skeleton>
  );
}

// Room Card Skeleton variant - matches new design with full-height image and overlaid content
// Visual hierarchy: name (0.15) > meta (0.12) > buttons/badge (0.08)
export function RoomCardSkeleton() {
  return (
    <Skeleton
      className="group overflow-hidden rounded-lg h-full flex flex-col"
      role="status"
      aria-label="Loading room"
    >
      <div className="relative h-full">
        {/* Room Photo - full card background with portrait aspect ratio */}
        <div className="-m-px aspect-[4/5] bg-white/[0.04]" />

        {/* Gradient overlay for text visibility */}
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />

        {/* Top overlay controls */}
        <div className="absolute top-3 left-3 right-3 sm:top-4 sm:left-4 sm:right-4 flex justify-between items-start z-20">
          {/* Favorite button skeleton - top left */}
          <div className="h-10 w-10 rounded-full bg-white/[0.08]" />

          {/* Price badge skeleton - top right */}
          <div className="h-7 sm:h-8 w-24 rounded-md bg-white/[0.08]" />
        </div>

        {/* Room info - overlaid on bottom */}
        <div className="absolute inset-x-0 bottom-0 p-4 sm:p-6 space-y-2 z-10">
          {/* Room name - primary element */}
          <div className="h-7 sm:h-8 w-3/4 bg-white/[0.15] rounded" />

          {/* Capacity and dimensions - secondary element */}
          <div className="h-4 w-2/5 bg-white/[0.12] rounded" />
        </div>
      </div>

      {/* Screen reader loading text */}
      <span className="sr-only">Loading room information...</span>
    </Skeleton>
  );
}
